import { useCallback, useEffect, useState } from "react";
import { ShieldCheck } from "lucide-react";
import { Button } from "./ui/button";
import { Dropzone } from "./ReceiptUpload";
import { ReceiptCard } from "./ReceiptCard";
import { ReviewDialog } from "./ReviewDialog";
import { useImportJob, ImportJobStatus } from "./ImportJob";
import { api } from "../lib/api";
import { fmtDate, fmtNum } from "../lib/format";

const TABS = [["unassigned", "Nicht zugeordnet"], ["assigned", "Zugeordnet"], ["all", "Alle"]];

export default function Inbox({ onDataChanged }) {
  const [state, setState] = useState("unassigned");
  const [list, setList] = useState([]);
  const [review, setReview] = useState(false);
  const load = useCallback(() => api.listReceipts(state).then(setList), [state]);
  useEffect(() => { load(); }, [load]);
  const reading = list.some((r) => r.ai_status === "pending" || r.ai_status === "running");
  useEffect(() => {
    if (!reading) return;
    const t = setInterval(load, 2500);
    return () => clearInterval(t);
  }, [reading, load]);
  const changed = () => { load(); onDataChanged(); };
  const imp = useImportJob("confirm", changed);

  const loadSafe = async () => (await api.safeSuggestions()).map((s) => ({
    key: s.receipt_id, receipt_id: s.receipt_id, transaction_id: s.transaction_id,
    label: `${s.receipt.extracted.vendor} → ${fmtDate(s.transaction.value_date)} ${fmtNum(s.transaction.amount)} €`,
    title: `${s.receipt.extracted.vendor} · ${fmtNum(s.receipt.extracted.gross_total)} ${s.receipt.extracted.currency}`,
    subtitle: `→ ${fmtDate(s.transaction.value_date)} · ${s.transaction.merchant} · ${fmtNum(s.transaction.amount)} €`,
    note: s.conflict ? "Achtung: Diese Buchung wird auch von einem anderen Beleg vorgeschlagen." : s.reason, warn: s.conflict,
  }));

  return (
    <div className="grid gap-5 p-4 sm:p-6 lg:p-8" data-testid="inbox-view">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-sm text-slate-500">Belege sammeln & zuordnen</div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">Belege-Eingang</h1>
        </div>
        <Button onClick={() => setReview(true)} disabled={imp.job?.status === "running"} data-testid="confirm-safe-suggestions-btn" className="gap-1.5 bg-emerald-700 hover:bg-emerald-800">
          <ShieldCheck className="h-4 w-4" /> Alle sicheren Vorschläge bestätigen
        </Button>
      </div>
      <ImportJobStatus job={imp.job} setJob={imp.setJob} testId="confirm-import-job" />
      <Dropzone onDone={changed} />
      <div className="flex gap-1 rounded-lg bg-slate-100 p-1 sm:w-fit" data-testid="inbox-tabs">
        {TABS.map(([k, l]) => (
          <button key={k} onClick={() => setState(k)} data-testid={`inbox-tab-${k}`}
            className={`flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition-colors sm:flex-none ${state === k ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"}`}>{l}</button>
        ))}
      </div>
      {list.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-500" data-testid="inbox-empty">
          {state === "unassigned" ? "Keine offenen Belege im Eingang." : "Keine Belege."}
        </div>
      ) : (
        <div className="grid items-start gap-3 xl:grid-cols-2" data-testid="receipt-list">
          {list.map((rc) => <ReceiptCard key={rc.id} rc={rc} onChanged={changed} />)}
        </div>
      )}
      <ReviewDialog open={review} onOpenChange={setReview} testId="safe-review-dialog"
        title="Sichere Vorschläge bestätigen"
        description="Nur Vorschläge mit hoher Sicherheit (Betrag, Händler und Datum passen). Abwählen, was nicht stimmt."
        load={loadSafe} applyLabel="Zuordnen"
        onApply={(items) => imp.start(items.map((i) => ({ receipt_id: i.receipt_id, transaction_id: i.transaction_id, _label: i.label })))} />
    </div>
  );
}
